import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';
import { useUploadCertification } from '../../hooks/useUploadCertification';
import type { SupplierPolicy, UserOrganisation } from '../../types/types';
import { Link } from 'react-router-dom';

interface SupplierProfileBuilderPoliciesProps {
  supplierId: string;
  onCountChange?: (count: number) => void;
}

const emptyForm = {
  policy_type: '',
  policy_name: '',
  file_url: '',
  is_public: true,
};

export function SupplierProfileBuilderPolicies({
  supplierId,
  onCountChange,
}: SupplierProfileBuilderPoliciesProps) {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const organisation = selectedOrganisation?.organisation;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const features = organisation?.plan?.features as
    | Record<string, boolean | number>
    | undefined;
  const profileMaxUploads: number | undefined =
    features?.profileMaxUploads != null
      ? Number(features.profileMaxUploads)
      : undefined;

  const [policies, setPolicies] = useState<SupplierPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState(emptyForm);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const {
    uploadCertification: uploadPolicyFile,
    uploading,
    error: uploadError,
  } = useUploadCertification();

  const documentBucket = useMemo(() => 'supplier-profile-assets', []);

  const fetchPolicies = async () => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('supplier_policies')
      .select('*')
      .eq('supplier_id', supplierId)
      .order('created_at', { ascending: false });

    if (fetchError) {
      captureSupabaseError(fetchError, { op: 'policies.fetch' });
      setError(fetchError.message);
      setPolicies([]);
    } else {
      const items = (data ?? []) as SupplierPolicy[];
      const enriched = await Promise.all(
        items.map(async (policy) => {
          if (!policy.file_url) return policy;
          if (policy.file_url.startsWith('http')) {
            return { ...policy, signed_url: policy.file_url };
          }
          const { data: urlData, error: urlError } = await supabase.storage
            .from(documentBucket)
            .createSignedUrl(policy.file_url, 60 * 5);
          if (urlError) {
            captureSupabaseError(urlError, { op: 'policies.signed-url' });
          }
          return { ...policy, signed_url: urlData?.signedUrl ?? null };
        }),
      );
      setPolicies(enriched);
      onCountChange?.(enriched.length);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchPolicies();
  }, [supplierId]);

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedFile(null);
    setEditingId(null);
    setFormErrors({});
    setFileInputKey((prev) => prev + 1);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const getPolicyUrl = (value: string | null) => {
    if (!value) return null;
    if (value.startsWith('http')) return value;
    const { data } = supabase.storage.from(documentBucket).getPublicUrl('');
    const baseUrl = data.publicUrl.replace(/\/$/, '');
    const encodedPath = value
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');
    return `${baseUrl}/${encodedPath}`;
  };

  // const handleEdit = (policy: Policy) => {
  //   setEditingId(policy.id);
  //   setForm({
  //     policy_type: policy.policy_type ?? '',
  //     policy_name: policy.policy_name ?? '',
  //     file_url: policy.file_url ?? '',
  //     is_public: policy.is_public ?? true,
  //   });
  //   setSelectedFile(null);
  //   setFileInputKey((prev) => prev + 1);
  //   setFormErrors({});
  // };

  const handleDelete = async (policy: SupplierPolicy) => {
    if (!confirm('Delete this policy?')) return;

    setSaving(true);
    setError(null);

    if (policy.file_url && !policy.file_url.startsWith('http')) {
      const { error: removeError } = await supabase.storage
        .from(documentBucket)
        .remove([policy.file_url]);

      if (removeError) {
        captureSupabaseError(removeError, { op: 'policies.file.delete' });
      }
    }

    const { error: deleteError } = await supabase
      .from('supplier_policies')
      .delete()
      .eq('id', policy.id);

    if (deleteError) {
      captureSupabaseError(deleteError, { op: 'policies.delete' });
      setError(deleteError.message);
    } else {
      await fetchPolicies();
    }

    setSaving(false);
  };

  const handleSave = async () => {
    const nextErrors: Record<string, string> = {};
    if (!form.policy_type.trim()) {
      nextErrors.policy_type = 'Policy type is required';
    }
    if (!form.file_url && !selectedFile) {
      nextErrors.file_url = 'Policy file is required';
    }

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return;
    }

    setSaving(true);
    setError(null);
    setFormErrors({});

    let filePath = form.file_url || null;

    if (selectedFile) {
      const uploadResult = await uploadPolicyFile(selectedFile, supplierId);
      if (!uploadResult) {
        setSaving(false);
        return;
      }
      filePath = uploadResult.storagePath;
    }

    const payload = {
      supplier_id: supplierId,
      policy_type: form.policy_type.trim(),
      policy_name: form.policy_name?.trim() || null,
      file_url: filePath,
      // is_public: form.is_public ?? true,
      is_public: true, // default to true
      updated_at: new Date().toISOString(),
    };

    const { error: saveError } = editingId
      ? await supabase
          .from('supplier_policies')
          .update(payload)
          .eq('id', editingId)
      : await supabase.from('supplier_policies').insert({
          ...payload,
          created_at: new Date().toISOString(),
        });

    if (saveError) {
      captureSupabaseError(saveError, { op: 'policies.save' });
      setError(saveError.message);
      setSaving(false);
      return;
    }

    resetForm();
    await fetchPolicies();
    setSaving(false);
    setShowModal(false);
  };

  return (
    <div className="py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Policies</h5>
        <Button
          variant="primary"
          onClick={openModal}
          disabled={
            profileMaxUploads !== undefined &&
            policies.length >= profileMaxUploads
          }
        >
          <i className="bi bi-plus-lg me-2"></i>
          Add policy
        </Button>
      </div>
      {profileMaxUploads !== undefined &&
        policies.length >= profileMaxUploads && (
          <Alert variant="info" className="mb-3">
            You have reached the maximum of {profileMaxUploads} files allowed on
            your current plan. If you wish to upgrade your plan, please goto{' '}
            <Link to="/settings?tab=organisation">Settings</Link> page.
          </Alert>
        )}

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : policies.length ? (
        <Table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              {/* <th>Visibility</th> */}
              <th>File</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((policy) => {
              const publicUrl =
                policy.signed_url ?? getPolicyUrl(policy.file_url);

              return (
                <tr key={policy.id}>
                  <td>{policy.policy_type}</td>
                  <td>{policy.policy_name || '—'}</td>
                  {/* <td>
                    {policy.is_public ? (
                      <span className="badge bg-success">Public</span>
                    ) : (
                      <span className="badge bg-secondary">Private</span>
                    )}
                  </td> */}
                  <td>
                    {publicUrl ? (
                      <a
                        href={publicUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary"
                      >
                        Download
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {/* <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => handleEdit(policy)}
                        disabled={saving || uploading}
                      >
                        Edit
                      </Button> */}
                      <Button
                        size="sm"
                        variant="outline-danger"
                        className="editor-icon-button"
                        onClick={() => handleDelete(policy)}
                        disabled={saving || uploading}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted">No policies added yet.</p>
      )}

      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? 'Update policy' : 'Add policy'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Policy type *</Form.Label>
            <Form.Control
              type="text"
              value={form.policy_type}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  policy_type: event.target.value,
                }))
              }
              isInvalid={Boolean(formErrors.policy_type)}
              placeholder="Code of Conduct"
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.policy_type}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Policy name</Form.Label>
            <Form.Control
              type="text"
              value={form.policy_name}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  policy_name: event.target.value,
                }))
              }
              placeholder="Supplier Code of Conduct 2025"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Upload policy *</Form.Label>
            <Form.Control
              key={fileInputKey}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              isInvalid={Boolean(formErrors.file_url)}
              onChange={(event) => {
                // @ts-expect-error ts(2339)
                if (event.target.files?.[0]) {
                  // @ts-expect-error ts(2339)
                  setSelectedFile(event.target.files[0]);
                }
              }}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.file_url}
            </Form.Control.Feedback>
            {selectedFile && (
              <div className="small text-muted mt-2">
                Selected: {selectedFile.name}
              </div>
            )}
            {uploadError && (
              <Alert variant="danger" className="mt-2">
                {uploadError}
              </Alert>
            )}
          </Form.Group>

          {/* <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="policy-public-switch"
              label="Publicly visible"
              checked={Boolean(form.is_public)}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  is_public: event.target.checked,
                }))
              }
            />
          </Form.Group> */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || uploading}
          >
            {saving || uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : editingId ? (
              'Update policy'
            ) : (
              'Add policy'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
